import { Box, Typography, useTheme } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { AnimatedBlueButton } from "src/components/common";

const JoinUs: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const slideList = [
    {
      id: 1,
      img: require("src/assets/images/home/campaign-creators-qCi_MzVODoU-unsplash.jpg"),
      title: t("home.joinUs.title1"),
      content: t("home.joinUs.content1"),
    },
    {
      id: 2,
      img: require("src/assets/images/home/kobu-agency-7okkFhxrxNw-unsplash.jpg"),
      title: t("home.joinUs.title2"),
      content: t("home.joinUs.content2"),
    },
    {
      id: 3,
      img: require("src/assets/images/home/christina-wocintechchat-com-eF7HN40WbAQ-unsplash.jpg"),
      title: t("home.joinUs.title3"),
      content: t("home.joinUs.content3"),
    },
  ];

  const handleClick = (index: number) => {
    setActiveIndex(index);
    if (intervalRef.current) clearInterval(intervalRef.current);
    startAutoSwitch();
  };

  const openMail = () => {
    window.location.href = "mailto:hr@shuluntc.com";
  };

  const startAutoSwitch = useCallback(() => {
    intervalRef.current = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % slideList.length);
    }, 10000);
  }, [slideList.length]);

  useEffect(() => {
    startAutoSwitch();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [startAutoSwitch]);

  return (
    <Box
      sx={{
        bgcolor: "#f9f9fb",
        paddingBlock: "clamp(86px,10.417vw,200px)",
        display: "flex",
        alignContent: "center",
        alignItems: "center",
        color: "#323e59",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "1920px",
          gridTemplateColumns: "repeat(12,1fr)",
          columnGap: "clamp(10px,.833vw,16px)",
          paddingInline: "clamp(16px,4.167vw,80px)",
          display: "grid",
          position: "relative",
          m: "0 auto",
          [theme.breakpoints.down("md")]: { paddingInline: "72px", display: "flex", flexDirection: "column" },
        }}
      >
        <Box
          className="header"
          sx={{
            gridArea: "1/1/2/13",
            gridTemplateColumns: "repeat(12,1fr)",
            display: "grid",
            gap: "clamp(14px,1.563vw,30px) clamp(10px,.833vw,16px)",
            [theme.breakpoints.down("sm")]: {
              display: "flex",
              gap: "14px",
              flexDirection: "column",
            },
          }}
        >
          <Typography
            sx={{
              fontSize: "clamp(28px,3.125vw,60px)",
              fontWeight: "500",
              lineHeight: 1.1,
              position: "absolute",
              left: "clamp(50px,4.688vw,90px)",
              color: "#6985b41f",
              zIndex: 1,
              [theme.breakpoints.down("md")]: { display: "none" },
            }}
          >
            05
          </Typography>
          <Typography sx={{ gridArea: "1/3/2/8", fontSize: "clamp(28px,3.125vw,60px)", fontWeight: "500" }}>
            {t("home.joinUs.title")}
          </Typography>
        </Box>
        <Box className="content" sx={{ gridArea: "3/3/4/12", height: "min-content", mt: "clamp(45px,4.167vw,80px)" }}>
          <Box
            sx={{
              gridTemplateColumns: "repeat(2,1fr)",
              gap: "clamp(30px,3.125vw,60px) clamp(10px,8.542vw,164px)",
              minHeight: "700px",
              display: "grid",
              position: "relative",
              [theme.breakpoints.down("md")]: {
                display: "flex",
                minHeight: "auto",
                flexDirection: "column",
              },
            }}
          >
            <Box
              className="slides-wrapper"
              sx={{ gridColumn: "2", flexDirection: "column", gap: "clamp(24px,2.083vw,40px)", display: "flex" }}
            >
              {slideList.map((item, index) => (
                <Box className="slide" key={item.id} sx={{ transition: "all 1s ease" }}>
                  <Box className="description">
                    <Box>
                      <Typography
                        onClick={() => handleClick(index)}
                        sx={{
                          color: activeIndex === index ? "#323e59" : "#9ba5b7f5",
                          cursor: "pointer",
                          transition: "all .3s ease-in-out",
                          display: "inline-flex",
                          position: "relative",
                          fontSize: "clamp(22px,1.667vw,32px)",
                          fontWeight: "500",
                          lineHeight: 1.1,
                          pointerEvents: activeIndex === index ? "none" : "auto",
                          [theme.breakpoints.down("md")]: {
                            color: "#364056",
                            width: "100%",
                            pr: "20px",
                            ":after": {
                              width: "14px",
                              height: "15px",
                              transition: "all .5s ease-in-out",
                              position: "absolute",
                              top: 0,
                              right: 0,
                              content: `" "`,
                              filter:
                                activeIndex === index
                                  ? "brightness(0)saturate()invert(25%)sepia(9%)saturate(1210%)hue-rotate(183deg)brightness(92%)contrast(90%)"
                                  : "brightness(0)saturate()invert(19%)sepia(99%)saturate(7447%)hue-rotate(221deg)brightness()contrast(102%)",
                              backgroundImage:
                                activeIndex === index
                                  ? "url(https://ambiq.com/wp-content/themes/ambiq/assets/img/icons/arrow-down-alt.svg)"
                                  : "url(https://ambiq.com/wp-content/themes/ambiq/assets/img/icons/arrow-up.svg)",
                            },
                          },
                        }}
                      >
                        {item.title}
                      </Typography>
                    </Box>
                    <AnimatePresence initial={false} mode="wait">
                      {activeIndex === index && (
                        <motion.div
                          key={item.id}
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{
                            height: { duration: 0.6, ease: "easeInOut" },
                            opacity: { duration: 0.3, ease: "easeInOut" },
                          }}
                          style={{ overflow: "hidden" }}
                        >
                          <Typography
                            sx={{
                              color: "#323e59",
                              pointerEvents: "none",
                              width: "clamp(200px,23.958vw,460px)",
                              pt: "clamp(8px,.729vw,14px)",
                              fontSize: "clamp(14px,.833vw,16px)",
                              fontWeight: "400",
                              lineHeight: 1.6,
                              whiteSpace: "pre-wrap",
                              [theme.breakpoints.down("md")]: { width: "90%" },
                            }}
                          >
                            {item.content}
                          </Typography>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Box>
                  <Box
                    className="slide-thumb fit-cover"
                    sx={{
                      clipPath: activeIndex === index ? "inset(0% round 2.5%)" : "inset(50% round 2.5%)",
                      opacity: activeIndex === index ? 1 : 0,
                      zIndex: activeIndex === index ? 1 : 0,
                      top: 0,
                      left: 0,
                      aspectRatio: "5607/3738",
                      pointerEvents: "none",
                      width: "clamp(315px,30vw,576px)",
                      transition: "all .8s ease-in-out",
                      position: "absolute",
                      overflow: "hidden",
                      img: {
                        width: "auto",
                        height: "130%",
                        transition: "opacity .5s",
                        transform: "translateY(-15%)",
                        objectFit: "cover",
                      },
                      [theme.breakpoints.down("md")]: {
                        width: "100%",
                        mt: "30px",
                        position: "relative",
                        right: "auto",
                        top: "auto",
                        left: "auto",
                        height: activeIndex === index ? "auto" : 0,
                        img: {
                          height: "100%",
                          width: "100%",
                          transform: "none",
                        },
                      },
                    }}
                  >
                    <img src={item.img} alt="img" />
                  </Box>
                </Box>
              ))}
            </Box>
            <Box sx={{ gridColumn: 2 }}>
              <AnimatedBlueButton text="Join Us" variant="outlined" onClick={openMail} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default JoinUs;
